import graphql from "babel-plugin-relay/macro";
import { Provider, useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import {
  CloseIcon,
  ArrowLeftIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";

import {
  currentPageAtom,
  modalShowSubTitleAtom,
  modalTitleAtom,
  onBackAtom,
} from "../atoms";
import { CurrentPackageDetail } from "./CurrentPackageDetail";
import { CurrentPackageOrder } from "./CurrentPackageOrder";
import { CurrentPackageOrderFooter } from "./CurrentPackageOrderFooter";
import { CurrentPackagesList } from "./CurrentPackagesList";
import { ModalLoader } from "./ModalLoader";
import { CurrentPackagesModal_club$key } from "./__generated__/CurrentPackagesModal_club.graphql";
import { PackageType } from "./__generated__/ModalLoaderQuery.graphql";

type Props = {
  isOpen: boolean;
  type: PackageType;
  onClose: () => void;
  clubRef: CurrentPackagesModal_club$key | null | undefined;
};

export const CurrentPackagesModal = ({
  isOpen,
  type,
  clubRef,
  onClose,
}: Props) => {
  return (
    <Provider>
      <CurrentPackagesModalInner
        isOpen={isOpen}
        type={type}
        onClose={onClose}
        clubRef={clubRef}
      />
    </Provider>
  );
};

const CurrentPackagesModalInner = ({
  clubRef,
  isOpen,
  type,
  onClose,
}: Props) => {
  const club = useFragment(CurrentPackagesModalFragmentString, clubRef);
  const clubName = club?.name;

  const onBack = useAtomValue(onBackAtom);
  const currentPage = useAtomValue(currentPageAtom);
  const modalTitle = useAtomValue(modalTitleAtom);
  const modalShowSubTitle = useAtomValue(modalShowSubTitleAtom);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onBack || onClose}
      CloseButtonIcon={onBack ? <ArrowLeftIcon /> : <CloseIcon />}
      upperTitle={modalTitle}
      subtitle={modalShowSubTitle ? clubName : undefined}
      hideFooterPadding
      disableContentScroll={currentPage === "loading"}
      content={
        {
          loading: <ModalLoader type={type} />,
          list: <CurrentPackagesList type={type} />,
          detail: <CurrentPackageDetail type={type} />,
          order: <CurrentPackageOrder />,
        }[currentPage]
      }
      footerContent={
        {
          loading: null,
          list: null,
          detail: null,
          order: <CurrentPackageOrderFooter />,
        }[currentPage]
      }
    />
  );
};

const CurrentPackagesModalFragmentString = graphql`
  fragment CurrentPackagesModal_club on Club {
    name
  }
`;
